import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/components/ui/toast';
import { useMe, useUpdateMe } from '@/features/settings/hooks';
import type { NotifChannel } from '@/features/settings/api';

type AlertType = 'divergence' | 'payment_overdue' | 'cert_expiring' | 'monthly_summary' | 'shift_summary';

const ALERT_TYPES: Array<{ key: AlertType; label: string; description: string }> = [
  { key: 'divergence', label: 'Divergência fiscal', description: 'Diferença acima da tolerância ao conferir relatório.' },
  { key: 'payment_overdue', label: 'Pagamento atrasado', description: 'NFS-e enviada há mais de 35 dias sem marcar como pago.' },
  { key: 'cert_expiring', label: 'Certificado expirando', description: '30 dias antes do vencimento do A1.' },
  { key: 'monthly_summary', label: 'Resumo mensal', description: 'Recebido no dia 1 de cada mês.' },
  { key: 'shift_summary', label: 'Resumo de plantão', description: 'Enviado ao encerrar o plantão.' },
];

const CHANNELS: Array<{ key: NotifChannel; label: string }> = [
  { key: 'push', label: 'Push' },
  { key: 'email', label: 'E-mail' },
];

type Matrix = Record<string, Record<NotifChannel, boolean>>;

function defaultMatrix(): Matrix {
  const result: Matrix = {};
  for (const t of ALERT_TYPES) {
    result[t.key] = { push: true, email: true, whatsapp: false };
  }
  return result;
}

export function NotificationMatrix() {
  const meQuery = useMe();
  const update = useUpdateMe();
  const toast = useToast();
  const [matrix, setMatrix] = useState<Matrix>(() => defaultMatrix());

  useEffect(() => {
    const remote = meQuery.data?.preferences?.notifMatrix;
    if (remote) {
      setMatrix({ ...defaultMatrix(), ...remote });
    }
  }, [meQuery.data]);

  const toggle = (type: AlertType, channel: NotifChannel) => {
    setMatrix((prev) => ({
      ...prev,
      [type]: { ...prev[type], [channel]: !prev[type][channel] },
    }));
  };

  const handleSave = async () => {
    try {
      await update.mutateAsync({ preferences: { notifMatrix: matrix } });
      toast.show({ title: 'Preferências salvas.', variant: 'success' });
    } catch (err) {
      toast.show({
        title: 'Erro ao salvar',
        description: err instanceof Error ? err.message : 'Tente novamente.',
        variant: 'error',
      });
    }
  };

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="font-semibold">Notificações</h3>
          <p className="text-sm text-(--color-fg-muted)">
            Escolha em quais canais receber cada tipo de alerta.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-(--color-fg-muted)">
                <th className="py-2">Alerta</th>
                {CHANNELS.map((c) => (
                  <th key={c.key} className="px-2 py-2 text-center">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ALERT_TYPES.map((t) => (
                <tr key={t.key} className="border-t border-(--color-border)">
                  <td className="py-2 pr-2">
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs text-(--color-fg-muted)">{t.description}</div>
                  </td>
                  {CHANNELS.map((c) => (
                    <td key={c.key} className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={matrix[t.key]?.[c.key] ?? false}
                        onChange={() => toggle(t.key, c.key)}
                        className="h-5 w-5 cursor-pointer accent-(--color-accent)"
                        aria-label={`${t.label} via ${c.label}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={update.isPending}>
            {update.isPending ? 'Salvando…' : 'Salvar preferências'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
